import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import {
  ProductPod,
  PodSticker,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  PodSection,
  ProductLink,
  ProductCustomOptions,
  PodSpacer,
  BundleDataModel
} from '@thd-olt-component-react/product-pod';
import { ProductSeeSimilarItems } from '@thd-olt-component-react/product-pod-group';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { ProductQuantity } from '@thd-olt-component-react/quantity';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { AddToVariations } from './AddToVariations';

export const CustomProductPodThreeColumn = ({
  itemId,
  storeId,
  position,
  onClick,
  hasInStoreFilter,
  itemRecsIndex,
  samplesLoading,
  impProductPodData,
  hasSameDayFilter,
  isFulfillmentFilterAndMsgEnabled
}) => {
  const [superSkuChangedItemIds, setSuperSkuChangedItemIds] = useState([]);
  const showFulfillmentPodStore = isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  const showFulfillmentPodShipping = isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;

  return (
    <ProductPod
      itemId={itemId}
      hasSSKU
      storeId={storeId}
      analyticsData={{ parent: 'plp', position }}
      analyticImpressionData={impProductPodData}
      render={(pod) => (
        <>
          <PodSection margin>
            <Row>
              <Col xs={3}>
                <PodSection noPadding columnAlign relative>
                  <PodSticker position="top-left">
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    <ProductSponsored itemId={pod.itemId} />
                  </PodSticker>
                  <ProductImage itemId={pod.itemId} />
                  <SuperSkuMiniSwatches
                    itemId={pod.itemId}
                    storeId={storeId}
                    onChange={(clickData) => {
                      if (!superSkuChangedItemIds.includes(clickData.itemId)) {
                        setSuperSkuChangedItemIds([...superSkuChangedItemIds, clickData.itemId]);
                      }
                      pod.onChange(clickData);
                    }}
                    onHover={pod.onSSKUHover}
                    componentImpressionData={
                      {
                        name: 'ProductPod',
                        component: 'ProductPod',
                        position: position + 1,
                        type: 'product'
                      }
                    }
                    isImpressionsRequired
                  />
                </PodSection>
              </Col>
              <Col xs={6}>
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  onSavingsCenterToggle={pod.onSavingsCenterToggle}
                  showPreferredPricingBadge
                  hideForProductTypes={['HDQC']}
                />
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <Promotion
                    itemId={pod.itemId}
                    hideConditionalPromoDescription
                    pageType="PLP"
                  />
                </PodSpacer>
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <ProductRating itemId={pod.itemId} />
                  <ProductIdentifier itemId={pod.itemId} type="model" />
                </PodSpacer>
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <ProductHeader
                    brand="above"
                    itemId={pod.itemId}
                    brandTitleMaxLine={4}
                    disableShopThisCollection
                    onClick={onClick}
                  />
                </PodSpacer>
                <PodSpacer padding={['TOP']}>
                  <KeyProductFeatures
                    hideImage
                    storeId={storeId}
                    itemId={pod.itemId}
                    maxfeatures={3}
                    simple
                    oneColumnGrid
                  />
                </PodSpacer>
              </Col>
              <Col xs={3}>
                {
                  showFulfillmentPodStore
                  && (
                    <PodSpacer padding={['TOP', 'BOTTOM']} hideForProductTypes={['HDQC']}>
                      <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                    </PodSpacer>
                  )
                }
                {
                  showFulfillmentPodShipping
                  && (
                    <PodSpacer padding={['TOP', 'BOTTOM']} hideForProductTypes={['HDQC']}>
                      <FulfillmentPodShipping
                        itemId={pod.itemId}
                        storeId={storeId}
                        hasSameDayFilter={hasSameDayFilter}
                        showFSDBMMessage
                      />
                    </PodSpacer>
                  )
                }
                <PodSpacer padding={['TOP', 'BOTTOM']} minHeight="4.8rem">
                  <div className="sui-flex sui-justify-center">
                    <OrderSample
                      disableDrawerCTA
                      disableSampleCall={
                        samplesLoading
                        || (pod.itemId !== itemId && !superSkuChangedItemIds.includes(pod.itemId))
                      }
                      itemId={pod.itemId}
                      link
                      showTitle={false}
                    />
                  </div>
                </PodSpacer>
                <PodSpacer padding={['TOP', 'BOTTOM']} minHeight="5.2rem">
                  <ProductQuantity
                    itemId={pod.itemId}
                    onQuantityChange={pod.onQuantityUpdate}
                    mediumWidth
                    wideView
                    noRightMargin
                  />
                </PodSpacer>
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <ProductATC
                    itemId={pod.itemId}
                    storeId={storeId}
                    checkGeneric
                    quantity={pod.quantity}
                    hasInStoreFilter={hasInStoreFilter}
                    variant="primary"
                    isB2BPLP
                  />
                </PodSpacer>
                <ProductSeeSimilarItems
                  itemId={pod.itemId}
                  position={itemRecsIndex}
                  productPodRef={pod.ref}
                  storeId={storeId}
                />
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <NonBuyableQuoteCenter itemId={pod.itemId} checkForProductSubType={['HDQC']} />
                </PodSpacer>
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <AddToVariations pod={pod} storeId={storeId} />
                </PodSpacer>
              </Col>
            </Row>
          </PodSection>
        </>
      )}
    />
  );
};

CustomProductPodThreeColumn.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  hasInStoreFilter: PropTypes.bool.isRequired,
  itemRecsIndex: PropTypes.number.isRequired,
  samplesLoading: PropTypes.bool.isRequired,
  impProductPodData: PropTypes.shape({}),
  hasSameDayFilter: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool
};

CustomProductPodThreeColumn.defaultProps = {
  impProductPodData: null,
  hasSameDayFilter: false,
  isFulfillmentFilterAndMsgEnabled: false
};

CustomProductPodThreeColumn.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  OrderSample,
  Price,
  Promotion,
  PriceClearanceDataModel,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductHeader,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  KeyProductFeatures,
  ProductSeeSimilarItems,
  ProductLink,
  ProductQuantity,
  NonBuyableQuoteCenter,
  BundleDataModel,
  AddToVariations
);
